import { makeStyles, ThemeProvider } from "@material-ui/styles";
import { ConnectedRouter } from "connected-react-router";
import { History } from "history";
import { SnackbarProvider, useSnackbar } from "notistack";
import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import { Provider } from "react-redux";
import { AnyAction, Store } from "redux";
import { ApplicationState } from ".";
import LanguageProvider from "./components/common/language/LanguageProvider";
import NavContainer from "./components/layout/NavContainer";
import Notifications from "./components/layout/Notifications";
import Routes from "./Routes";
import theme from "./theme";
import { Configuration, EventMessage, EventType, PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { getLastUserType } from "./store/session/SessionStore";
import { fetchUiAppRegistration } from "./api/SettingsApi";
import { LoadingSpinner } from "./components/common/LoadingSpinner";
import UrlHelper from "./utils/UrlHelpler";
import { setUpUser } from "./api/UserApi";
import * as Msal from "@azure/msal-browser";
import { getAccessToken, getAccount } from "./utils/AuthHelper";
import { IconButton, Theme } from "@material-ui/core";
import Close from "@material-ui/icons/Close";

const useStepStyles = makeStyles((theme: Theme) => ({
  closeButton: {
    color: theme.palette.primary.contrastText,
    backgroundColor: "transparent",
  },
}));

const Root = ({ history, store }: { history: History<any>; store: Store<ApplicationState, AnyAction> }) => {
  const [clientApp, setClientApp] = useState<PublicClientApplication | null>(null);
  let registering = false;

  useEffect(() => {
    const type = getLastUserType() ?? "EXTERNAL";
    if (!registering) {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      registering = true;

      fetchUiAppRegistration(type)
        .then(async (config) => {
          store.dispatch({ type: "SET_APP_REGISTRATION", appRegistration: config });
          const configuration: Configuration = {
            auth: {
              authority: config.authority,
              clientId: config.clientId,
              redirectUri: UrlHelper.loginUrl(),
              knownAuthorities: [config.authority.split("/")[2]],
            },
            cache: {
              cacheLocation: "localStorage", // Use localStorage to persist the session across tabs
            },
          };
          const authClient = new PublicClientApplication(configuration);
          await authClient.initialize();

          authClient.addEventCallback(async (message: EventMessage) => {
            if (message.eventType === EventType.LOGIN_SUCCESS && type === "EXTERNAL") {
              await setUpUser();
            }
          });

          store.dispatch({ type: "SET_AUTH_CLIENT", authClient: authClient });
          setClientApp(authClient);

          // To avoid the "Something went wrong" error message on new tabs we implemented the following workaround
          // causing the application to authenticate using acquireTokenRedirect as acquireTokenSilent
          // fails on new tabs but without involving the globalErrorHandler.
          const silentRequest: Msal.SilentRequest = {
            scopes: config.scopes,
            redirectUri: UrlHelper.loginUrl(),
            account: getAccount(authClient),
          };
          await getAccessToken("Root.tsx", authClient, silentRequest);
        })
        .finally(() => (registering = false));
    }
  }, [store]);

  const SnackbarCloseButton = ({ snackbarKey }: { snackbarKey: string | number }) => {
    const { closeSnackbar } = useSnackbar();
    const classes = useStepStyles();

    return (
      <IconButton onClick={() => closeSnackbar(snackbarKey)} className={classes.closeButton} size="small">
        <Close />
      </IconButton>
    );
  };

  return (
    <ThemeProvider theme={theme}>
      <Provider store={store}>
        {clientApp !== null && (
          <MsalProvider instance={clientApp}>
            <ConnectedRouter history={history}>
              <LanguageProvider>
                <NavContainer>
                  <SnackbarProvider
                    anchorOrigin={{ vertical: "top", horizontal: "center" }}
                    preventDuplicate={true}
                    action={(snackbarKey) => <SnackbarCloseButton snackbarKey={snackbarKey} />}
                  >
                    <React.Fragment>
                      <Notifications />
                      <Routes />
                    </React.Fragment>
                  </SnackbarProvider>
                </NavContainer>
              </LanguageProvider>
            </ConnectedRouter>
          </MsalProvider>
        )}
        {clientApp === null && <LoadingSpinner />}
      </Provider>
    </ThemeProvider>
  );
};

Root.propTypes = {
  store: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
};

export default Root;
